import React, { useState } from "react";
import { Layout, Popover, Badge, Tooltip } from "antd";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  WifiOutlined, 
  DisconnectOutlined,
  BellOutlined,
  EnvironmentOutlined 
} from "@ant-design/icons";

import CustomScrollbars from "util/CustomScrollbars";
import languageData from "./languageData";
import { switchLanguage, toggleCollapsedSideNav } from "../../appRedux/actions";
import SearchBox from "../../components/SearchBox";
import UserInfo from "../../components/UserInfo";
import AppNotification from "../../components/AppNotification";
import MailNotification from "../../components/MailNotification";
import Auxiliary from "util/Auxiliary";

import {
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_MINI_SIDEBAR,
  TAB_SIZE,
} from "../../constants/ThemeSetting";
import { useDispatch, useSelector } from "react-redux";
import SidebarLogo from "../Sidebar/SidebarLogo";

const { Header } = Layout;

const Topbar = ({ mqttConnected = false, activeTrackers = 0, totalAlerts = 0 }) => {
  const { locale, navStyle } = useSelector(({ settings }) => settings);
  const navCollapsed = useSelector(({ common }) => common.navCollapsed);
  const width = useSelector(({ common }) => common.width);
  const [searchText, setSearchText] = useState("");
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const location = useLocation();

  const handleLanguageChange = (language) => {
    dispatch(switchLanguage(language));
    i18n.changeLanguage(language.locale);
    localStorage.setItem('appLanguage', language.locale);
  };

  // Breadcrumbs simples basados en la ruta
  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path.includes('tracking')) return 'Tracking';
    if (path.includes('paths')) return 'Paths';
    if (path.includes('proximity')) return t('Proximidad') || 'Proximidad';
    if (path.includes('settings')) return t('Configuración') || 'Settings';
    return 'Dashboard';
  };

  const languageMenu = () => (
    <CustomScrollbars className="gx-popover-lang-scroll">
      <ul className="gx-sub-popover">
        {languageData.map((language) => (
          <li
            className="gx-media gx-pointer"
            key={JSON.stringify(language)}
            onClick={() => handleLanguageChange(language)}
          >
            <i className={`flag flag-24 gx-mr-2 flag-${language.icon}`} />
            <span className="gx-language-text">{language.name}</span>
          </li>
        ))}
      </ul>
    </CustomScrollbars>
  );

  const updateSearchChatUser = (evt) => {
    setSearchText(evt.target.value);
  };

  return (
    <Header style={{ flexWrap: "nowrap" }}>
      {navStyle === NAV_STYLE_DRAWER ||
      ((navStyle === NAV_STYLE_FIXED || navStyle === NAV_STYLE_MINI_SIDEBAR) &&
        width < TAB_SIZE) ? (
        <div className="gx-linebar gx-mr-3">
          <i
            className="gx-icon-btn icon icon-menu"
            onClick={() => {
              dispatch(toggleCollapsedSideNav(!navCollapsed));
            }}
          />
        </div>
      ) : null}
      <Link to="/" className="gx-d-block gx-d-lg-none gx-pointer">
        <img alt="logo2" src={"/assets/images/logo.png"} width="35%" />
      </Link>

      {/* Breadcrumb */}
      {width >= TAB_SIZE && (
        <div className="gx-d-none gx-d-lg-flex gx-align-items-center" style={{ marginLeft: '20px', color: '#8c8c8c', fontSize: '14px' }}>
          <EnvironmentOutlined style={{ marginRight: '8px' }} />
          <span style={{ fontWeight: 500 }}>{getBreadcrumb()}</span>
        </div>
      )}

      <ul className="gx-header-notifications gx-ml-auto">
        {width >= TAB_SIZE && (
          <li className="gx-notify" style={{ paddingRight: '15px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                borderRadius: '8px',
                backgroundColor: mqttConnected ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 77, 79, 0.08)',
                border: `1px solid ${mqttConnected ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 77, 79, 0.2)'}`,
                transition: 'all 0.3s ease',
              }}
            >
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '2px',
                  backgroundColor: mqttConnected ? '#4CAF50' : '#ff4d4f',
                  boxShadow: mqttConnected ? '0 0 6px rgba(76, 175, 80, 0.4)' : '0 0 6px rgba(255, 77, 79, 0.4)',
                  transition: 'all 0.3s ease',
                }}
              />
              <span style={{ 
                fontSize: '12px', 
                fontWeight: 600,
                color: mqttConnected ? '#4CAF50' : '#ff4d4f',
                letterSpacing: '0.2px',
              }}>
                {mqttConnected ? t("En línea") : t("Fuera de línea")}
              </span>
            </div>
          </li>
        )}

        {/* Contador de trackers activos */}
        {width >= TAB_SIZE && activeTrackers > 0 && (
          <li className="gx-notify" style={{ paddingRight: '15px' }}>
            <Tooltip title="Trackers activos">
              <Badge count={activeTrackers} showZero style={{ backgroundColor: '#1890ff' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, marginRight: '8px' }}>
                  Trackers
                </span>
              </Badge>
            </Tooltip>
          </li>
        )}

        {/* Alertas */}
        {width >= TAB_SIZE && (
          <li className="gx-notify" style={{ paddingRight: '15px' }}>
            <Tooltip title="Alertas pendientes">
              <Badge count={totalAlerts} showZero overflowCount={99}>
                <BellOutlined style={{ fontSize: '18px', color: totalAlerts > 0 ? '#ff4d4f' : '#8c8c8c' }} />
              </Badge>
            </Tooltip>
          </li>
        )}

        {/* Selector de idioma */}
        <li className="gx-language">
          <Popover
            overlayClassName="gx-popover-horizantal"
            placement="bottomRight"
            content={languageMenu()}
            trigger="click"
          >
            <span className="gx-pointer gx-flex-row gx-align-items-center">
              <i className={`flag flag-24 flag-${locale.icon}`} />
              {width >= TAB_SIZE && (
                <>
                  <span className="gx-pl-2 gx-language-name">{locale.name}</span>
                  <i className="icon icon-chevron-down gx-pl-2" />
                </>
              )}
            </span>
          </Popover>
        </li>

        {/* User Info */}
        {width >= TAB_SIZE ? (
          <li className="gx-user-nav">
            <UserInfo />
          </li>
        ) : (
          <Auxiliary>
            <li className="gx-user-nav">
              <UserInfo />
            </li>
          </Auxiliary>
        )}
      </ul>
    </Header>
  );
};

export default Topbar;
